import type { DataSession } from "../../data/core/session";
import { useData } from "../../runtime/provider";
import { useEffect, useRef, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import type { BuyerSession, CheckoutGoods } from "@libs/checkout-core";
import { TICKETS_SHEET_CHECKOUT_APPEARANCE } from "@libs/shared-ui";
// Not the `/web` barrel: its other components bring web asset typings.
import { EngineCheckoutEmbed } from "@libs/shared-ui/engine-checkout-embed";
import type {
  CheckoutTokenRequest,
  EngineCheckoutSucceeded,
} from "@libs/shared-ui/engine-checkout-embed";
import LPressable from "@components/LPressable";
import { refreshAccesses } from "@utils/stripe/enginePayment";
import { EXPIRED_MESSAGE } from "./checkoutView";
import {
  checkoutSessionIdOf,
  type EngineCheckoutSession,
} from "./engineSession";
import { BACK_TO_TICKETS } from "./native/CheckoutShape";
import { Chevron } from "./native/Chevron";
import { colors } from "./native/colors";
import { HeldFor } from "./native/HeldFor";
import { PayStatusButton } from "./native/PayStatusButton";
import { LOADING_BUTTON } from "./native/sheetButton";
import { SuccessView } from "./native/SuccessView";
import type { CheckoutChrome } from "./native/sheetButton";
import { useHoldRelease } from "./useHoldRelease";

/**
 * app-web's checkout in the tickets sheet (LIS-975): the checkout's own
 * web drop-in, as Link in Bio embeds it, under the sheet's back and
 * "Held for". The fan is signed in by now (the sheet asks a signed-out fan
 * for their address and a code first), so they are the host's to answer
 * for: every token the checkout asks for comes from the app's sign-in over
 * tRPC, as the native checkout's do (the first already on its way as the
 * drop-in loads), and the checkout shows the fan's address instead of
 * asking for one.
 *
 * "You're going" becomes the sheet's own success screen; the hold running
 * out goes back to the tickets; leaving before paying gives the hold back.
 */
export function SheetCheckout({
  session,
  onBack,
  onClose,
  onChrome,
  onSucceeded,
  backLabel = BACK_TO_TICKETS,
}: {
  /** Unused on the web: the drop-in draws its own shape. */
  free?: boolean;
  /** Unused on the web: the drop-in names the goods from its session. */
  goods?: CheckoutGoods;
  session: EngineCheckoutSession;
  /** Back to the tickets, with a message for them when there is one. */
  onBack: (message?: string) => void;
  /** Done with the sheet: back to the event's details. */
  onClose: () => void;
  /** The drop-in draws its own Pay button, "Loading checkout" until it is
   *  ready: the sheet draws none, and keeps no height. */
  onChrome?: (chrome: CheckoutChrome) => void;
  /** The order is the fan's: told as the drop-in says so. */
  onSucceeded?: () => void;
  /** What going back is called (the native checkout's `backLabel`). */
  backLabel?: string;
}) {
  const data = useData();
  useEffect(() => {
    onChrome?.({ button: null, hold: false });
  }, [onChrome]);
  const [until, setUntil] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    session: BuyerSession;
    method: string | null;
    email: string | null;
  } | null>(null);
  const paid = useRef(false);
  const payingNow = useRef(false);
  const expired = useRef(false);

  useHoldRelease(
    session,
    () => !paid.current && !payingNow.current && !expired.current,
  );

  const pay = (moving: boolean) => {
    payingNow.current = moving;
    setPaying(moving);
  };
  const succeeded = (result: EngineCheckoutSucceeded) => {
    paid.current = true;
    onSucceeded?.();
    // The sheet's success screen draws the order from the session itself,
    // read through the API: the engine answers no cross-origin call from
    // the app.
    data.api.accesses.getEngineCheckout
      .query({ accessId: session.accessId, clientSecret: session.clientSecret })
      .then((buyer) =>
        setSuccess({
          session: buyer,
          method: result.method_label,
          email: result.buyer_email ?? buyer.buyer_email ?? null,
        }),
      )
      .catch(onClose);
  };

  if (success)
    return (
      <SuccessView
        session={success.session}
        method={success.method}
        email={success.email}
        onDone={onClose}
      />
    );
  if (error)
    return (
      <View style={styles.screen}>
        <Text accessibilityRole="header" style={styles.endTitle}>
          {error}
        </Text>
        <LPressable
          accessibilityRole="button"
          onPress={() => onBack()}
          sentryEventName="SheetCheckout.Error"
          style={styles.secondary}
        >
          <Text style={styles.secondaryText}>{backLabel}</Text>
        </LPressable>
      </View>
    );
  return (
    <View style={styles.screen} testID="web-checkout">
      {paying ? null : (
        <View style={styles.header}>
          <LPressable
            accessibilityLabel={backLabel}
            accessibilityRole="button"
            hitSlop={10}
            onPress={() => onBack()}
            sentryEventName="SheetCheckout.Back"
            style={styles.back}
          >
            <Chevron />
          </LPressable>
          {until ? <HeldFor until={until} /> : null}
        </View>
      )}
      <EngineCheckoutEmbed
        url={session.url}
        secret={session.clientSecret}
        timing={session.timing}
        appearance={TICKETS_SHEET_CHECKOUT_APPEARANCE}
        identity="host"
        // The address is the fan's sign-in: no × on it, as in the app.
        contact="fixed"
        checkoutToken={(request) => askToken(data, request)}
        // The first attestation's token, asked for now the session exists
        // — alongside checkout.js and the frame loading, not once the frame
        // asks — and handed over as the answer to that first request.
        attestation={() => firstToken(data, session.clientSecret)}
        // Until the drop-in has loaded: its shape, and the button saying
        // what it has since Continue.
        placeholder={
          <View style={styles.placeholder}>
            <View style={styles.shape} />
            <PayStatusButton {...LOADING_BUTTON} />
          </View>
        }
        onError={setError}
        onHold={setUntil}
        onPaying={pay}
        onPaid={() => {
          paid.current = true;
          refreshAccesses(data).catch(() => undefined);
        }}
        onSucceeded={succeeded}
        onExpired={() => {
          expired.current = true;
          onBack(EXPIRED_MESSAGE);
        }}
      />
    </View>
  );
}

/** The app's own sign-in answers the checkout's token requests. */
const askToken = (data: DataSession, request: CheckoutTokenRequest) =>
  data.api.identity.checkoutToken
    .mutate({
      checkoutSessionId: request.sessionId,
      consents: request.consents as ["card_reuse:lissen:card"],
      reverify: request.reverify,
    })
    .then(({ token }) => token);

/** The token the checkout asks for first: the attestation, no consents. */
const firstToken = async (data: DataSession, clientSecret: string) =>
  askToken(data, {
    sessionId: checkoutSessionIdOf(clientSecret),
    consents: [],
    reverify: false,
  });

const styles = StyleSheet.create({
  screen: { gap: 12 },
  header: { alignItems: "center", flexDirection: "row", gap: 8, minHeight: 28 },
  back: {
    alignItems: "center",
    height: 28,
    justifyContent: "center",
    marginLeft: -6,
    width: 28,
  },
  placeholder: {
    ...StyleSheet.absoluteFill,
    gap: 12,
    justifyContent: "space-between",
  },
  shape: { backgroundColor: colors.fill, borderRadius: 12, flex: 1 },
  endTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: "600",
    marginBottom: 6,
  },
  secondary: {
    alignItems: "center",
    borderColor: colors.border,
    borderRadius: 12,
    borderWidth: 1,
    justifyContent: "center",
    minHeight: 44,
  },
  secondaryText: { color: colors.primary, fontSize: 15, fontWeight: "600" },
});
